/**
 * <quick-add-drawer>
 * -------------------
 * Single, page-level drawer shared by every quick-add button on the page: a
 * bottom sheet below lg, a right-hand panel from lg. There is exactly one
 * instance (see snippets/quick-add-drawer.liquid, rendered once in
 * layout/theme.liquid) — opening it from a different card just re-fetches and
 * swaps its content, it never clones the drawer itself.
 *
 * Extends the registered `sidebar-drawer` element (assets/sidebar-drawer.js) to
 * reuse its escape-key, overlay-click and swipe-to-close wiring, overriding
 * `toggleState` to own focus management, `aria-hidden`/`inert` and the scroll
 * lock. The slide itself is CSS (`.drawer--bottom-mobile`). `SidebarDrawer` is
 * declared inside its registration guard, so it isn't a global: the base class
 * comes from `customElements.get('sidebar-drawer')`. It must be registered
 * first — it is a deferred <head> script in layout/theme.liquid and deferred
 * scripts run in document order, so this file always evaluates after it.
 *
 * `SidebarDrawer` has no delegated `[data-drawer-open]` handling, so the open
 * and close listeners are owned here.
 *
 * The title comes from the clicked card, so the dialog is named the moment it
 * opens. Content is the real product, rendered by sections/quick-add.liquid and
 * fetched via the Section Rendering API (`{productUrl}?section_id=quick-add`).
 * The fetched <product-info> carries the variant picker, price, quantity, buy
 * button and the scripts it needs; injecting it with
 * HTMLUpdateUtility.setInnerHTML re-executes those scripts so variant switching
 * and add-to-cart work as they do on the PDP.
 *
 * Perceived speed: hovering, touching or focusing a product card prefetches the
 * section HTML, so by the time the shopper clicks the drawer usually renders
 * instantly.
 */

if (!customElements.get('quick-add-drawer')) {
  class QuickAddDrawer extends customElements.get('sidebar-drawer') {
    static FOCUSABLE_SELECTOR =
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    static PREFETCH_ROOT_MARGIN = '200px 0px';
    static PREFETCH_MAX_CONCURRENT = 3;
    static PREFETCH_IDLE_TIMEOUT = 2000;
    static PREFETCH_FALLBACK_DELAY = 500;
    static DESKTOP_QUERY = '(min-width: 1024px)';

    connectedCallback() {
      super.connectedCallback();

      this.contentEl = this.querySelector('[data-quick-add-drawer-content]');
      this.skeletonEl = this.querySelector('[data-quick-add-drawer-skeleton]');
      this.errorEl = this.querySelector('[data-quick-add-drawer-error]');
      this.retryButton = this.querySelector('[data-quick-add-retry]');
      this.closeButton = this.querySelector('[data-drawer-close]');
      this.titleEl = this.querySelector('[data-quick-add-drawer-title]');
      this.desktopQuery = window.matchMedia(QuickAddDrawer.DESKTOP_QUERY);

      this.openedBy = null;
      this.abortController = null;
      this.cache = {};
      this.pending = {};
      this.prefetchFailed = new Set();
      this.currentUrl = null;
      this.prefetchQueue = [];
      this.prefetchActiveCount = 0;

      document.addEventListener('click', this.handleOpenClick, true);
      document.addEventListener('mouseover', this.handlePrefetchIntent);
      document.addEventListener('touchstart', this.handlePrefetchIntent, {
        passive: true,
      });
      document.addEventListener('focusin', this.handlePrefetchIntent);
      this.closeButton?.addEventListener('click', this.handleCloseClick);
      this.retryButton?.addEventListener('click', this.handleRetry);
      this.addEventListener('keydown', this.handleKeydown);
      this.desktopQuery.addEventListener('change', this.syncDrawerPosition);
      this.cartUpdateUnsubscriber = subscribe(
        PUB_SUB_EVENTS.cartUpdate,
        this.handleCartUpdate
      );

      this.syncDrawerPosition();
      this.scheduleViewportPrefetch();
    }

    disconnectedCallback() {
      super.disconnectedCallback();

      document.removeEventListener('click', this.handleOpenClick, true);
      document.removeEventListener('mouseover', this.handlePrefetchIntent);
      document.removeEventListener('touchstart', this.handlePrefetchIntent);
      document.removeEventListener('focusin', this.handlePrefetchIntent);
      this.closeButton?.removeEventListener('click', this.handleCloseClick);
      this.retryButton?.removeEventListener('click', this.handleRetry);
      this.removeEventListener('keydown', this.handleKeydown);
      this.desktopQuery.removeEventListener('change', this.syncDrawerPosition);

      window.removeEventListener('load', this.handleWindowLoad);
      this.cancelScheduledPrefetch?.();
      this.prefetchObserver?.disconnect();
      this.cardMutationObserver?.disconnect();
      this.abortController?.abort();
      this.cartUpdateUnsubscriber?.();
    }

    // Viewport prefetch waits for the page `load` event plus an idle moment, so
    // its section fetches and image preloads never compete with the page's own
    // LCP resources. Intent prefetch (hover/touch/focus) is unaffected.
    scheduleViewportPrefetch() {
      if (document.readyState === 'complete') {
        this.handleWindowLoad();
      } else {
        window.addEventListener('load', this.handleWindowLoad, { once: true });
      }
    }

    handleWindowLoad = () => {
      if ('requestIdleCallback' in window) {
        const id = requestIdleCallback(() => this.setupViewportPrefetch(), {
          timeout: QuickAddDrawer.PREFETCH_IDLE_TIMEOUT,
        });
        this.cancelScheduledPrefetch = () => cancelIdleCallback(id);
      } else {
        const id = setTimeout(
          () => this.setupViewportPrefetch(),
          QuickAddDrawer.PREFETCH_FALLBACK_DELAY
        );
        this.cancelScheduledPrefetch = () => clearTimeout(id);
      }
    };

    // Viewport-driven prefetch — the mobile counterpart to hover intent, where
    // there is no hover and touchstart fires too late (at the tap itself).
    // Cards are prefetched shortly before they scroll into view, through a small
    // queue so a fast scroll can't fan out dozens of section requests at once.
    // A MutationObserver keeps AJAX-rendered cards (facet re-renders, load-more)
    // observed without any coupling to facets.js.
    setupViewportPrefetch() {
      if (!('IntersectionObserver' in window)) return;

      this.prefetchObserver = new IntersectionObserver(
        this.handleCardIntersection,
        { rootMargin: QuickAddDrawer.PREFETCH_ROOT_MARGIN }
      );

      this.observeCards(document);

      if ('MutationObserver' in window) {
        this.cardMutationObserver = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
              if (node instanceof Element) this.observeCards(node);
            });
          });
        });
        this.cardMutationObserver.observe(document.body, {
          childList: true,
          subtree: true,
        });
      }
    }

    observeCards(root) {
      if (root.matches?.('[data-quick-add-open]')) {
        this.prefetchObserver.observe(root);
      }
      root.querySelectorAll?.('[data-quick-add-open]').forEach((opener) => {
        this.prefetchObserver.observe(opener);
      });
    }

    handleCardIntersection = (entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;

        this.prefetchObserver.unobserve(entry.target);
        const url = entry.target.dataset.productUrl;
        if (!url) return;

        this.enqueuePrefetch(url);
      });
    };

    enqueuePrefetch(url) {
      if (navigator.connection?.saveData) return;
      if (this.cache[url] || this.pending[url] || this.prefetchFailed.has(url)) {
        return;
      }
      if (this.prefetchQueue.includes(url)) return;

      this.prefetchQueue.push(url);
      this.drainPrefetchQueue();
    }

    drainPrefetchQueue() {
      while (
        this.prefetchActiveCount < QuickAddDrawer.PREFETCH_MAX_CONCURRENT &&
        this.prefetchQueue.length
      ) {
        const url = this.prefetchQueue.shift();
        if (this.cache[url] || this.pending[url]) continue;

        this.prefetchActiveCount += 1;
        this.fetchProduct(url)
          .catch(() => {
            this.prefetchFailed.add(url);
          })
          .finally(() => {
            this.prefetchActiveCount -= 1;
            this.drainPrefetchQueue();
          });
      }
    }

    // Swipe-to-close follows the layout: down for the bottom sheet, right for
    // the side panel. The swipe helper reads this attribute on every gesture.
    syncDrawerPosition = () => {
      this.dataset.drawerPosition = this.desktopQuery.matches
        ? 'right'
        : 'bottom';
    };

    // Overrides SidebarDrawer.toggleState: `.open` drives the CSS slide (up on
    // mobile, in from the right on desktop), plus focus management for a11y.
    toggleState(isOpen) {
      this.isOpen = isOpen;
      this.handleToggleBodyOverflow(isOpen);

      const method = isOpen ? 'add' : 'remove';
      this.classList[method]('open');
      this.closest('.drawer-container')?.classList[method]('open');

      this.setAttribute('aria-hidden', String(!isOpen));
      if (isOpen) {
        this.removeAttribute('inert');
        this.focusFirstElement();
      } else {
        this.setAttribute('inert', '');
        this.openedBy?.focus();
        this.openedBy?.removeAttribute('data-quick-add-active');
        this.openedBy = null;
      }
    }

    // The parent's argument-less toggle() flips blindly and desyncs when another
    // drawer is already holding the scroll lock, so set it from the open state.
    handleToggleBodyOverflow(isOpen) {
      document.body.classList.toggle('no-scroll', isOpen);
    }

    // Delegated on `document` so this fires for any current or future
    // `[data-quick-add-open]` trigger, including AJAX-rendered product cards.
    // Capture phase, because the cart drawer stops clicks inside it bubbling.
    handleOpenClick = (event) => {
      const opener = event.target.closest?.('[data-quick-add-open]');
      if (!opener) return;

      // The card's <form> is the no-JS fallback — stop it submitting now that
      // the drawer is taking over.
      event.preventDefault();
      this.openedBy = opener;
      // Keeps the card's hover-only button visible while the drawer is open, so
      // focus can return to it on close (see `.card-product-cta`).
      opener.setAttribute('data-quick-add-active', '');
      this.setTitle(opener);
      this.open();
      this.loadProduct(opener.dataset.productUrl);
    };

    setTitle(opener) {
      if (!this.titleEl) return;

      this.titleEl.textContent = opener.dataset.productTitle || '';
      this.titleEl.href = opener.dataset.productUrl;
    }

    handleCloseClick = () => {
      this.close();
    };

    handleKeydown = (event) => {
      if (event.key === 'Tab' && this.classList.contains('open')) {
        this.trapFocus(event);
      }
    };

    handleRetry = () => {
      if (this.openedBy?.dataset.productUrl) {
        this.loadProduct(this.openedBy.dataset.productUrl);
      }
    };

    // product-form.js publishes cartUpdate before it renders the cart, so the
    // quick-add drawer closes as the cart drawer opens over it.
    handleCartUpdate = () => {
      if (this.classList.contains('open')) this.close();
    };

    trapFocus(event) {
      const focusable = Array.from(
        this.querySelectorAll(QuickAddDrawer.FOCUSABLE_SELECTOR)
      ).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    focusFirstElement() {
      requestAnimationFrame(() => {
        (this.closeButton || this).focus();
      });
    }

    async loadProduct(url) {
      if (!url) {
        this.renderError();
        return;
      }

      // Token guard: if another product is opened while this one is still
      // loading, the stale load must not render. The fetch itself is left
      // running so it can still populate the cache.
      this.currentUrl = url;

      // Read-through cache — no skeleton flash for an already-loaded product.
      if (this.cache[url]) {
        this.renderContent(this.cache[url]);
        return;
      }

      this.renderLoading();

      try {
        const html = await this.fetchProduct(url);
        if (this.currentUrl !== url) return;
        this.renderContent(html);
      } catch (error) {
        if (error.name === 'AbortError') return;
        if (this.currentUrl !== url) return;
        this.reportError(error);
        this.renderError();
      }
    }

    // Fires for hover, first touch and keyboard focus anywhere on a product card
    // or directly on a trigger — warms the section cache and the image cache
    // before the shopper clicks. Explicit intent bypasses the viewport queue.
    handlePrefetchIntent = (event) => {
      if (navigator.connection?.saveData) return;

      const target = event.target;
      if (!(target instanceof Element)) return;

      const opener =
        target.closest('[data-quick-add-open]') ||
        target.closest('.card-product')?.querySelector('[data-quick-add-open]');
      const url = opener?.dataset.productUrl;
      if (
        !url ||
        this.cache[url] ||
        this.pending[url] ||
        this.prefetchFailed.has(url)
      ) {
        return;
      }

      this.fetchProduct(url).catch(() => {
        // Prefetch failures are silent — a real open retries via loadProduct.
        this.prefetchFailed.add(url);
      });
    };

    // Single-flight fetch of the quick-add section, shared by prefetch and open.
    // Caches the <product-info> HTML on success.
    fetchProduct(url) {
      if (this.cache[url]) return Promise.resolve(this.cache[url]);
      if (this.pending[url]) return this.pending[url];

      if (!this.abortController) this.abortController = new AbortController();

      const requestUrl = new URL(url, window.location.origin);
      requestUrl.searchParams.set('section_id', 'quick-add');

      this.pending[url] = fetch(requestUrl, {
        signal: this.abortController.signal,
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(
              `Quick add request failed with status ${response.status}`
            );
          }
          return response.text();
        })
        .then((responseText) => {
          const parsedDocument = new DOMParser().parseFromString(
            responseText,
            'text/html'
          );
          const productInfo = parsedDocument.querySelector('product-info');
          if (!productInfo) {
            throw new Error('Quick add: <product-info> not found in response');
          }

          const html = productInfo.outerHTML;
          this.cache[url] = html;
          return html;
        })
        .finally(() => {
          delete this.pending[url];
        });

      return this.pending[url];
    }

    renderContent(html) {
      if (!this.contentEl) return;

      try {
        // setInnerHTML (not a plain innerHTML) so the <script> tags bundled inside
        // the fetched <product-info> execute — that is what wires up variant
        // switching (product-info.js) and add-to-cart (product-form.js). Both are
        // define-guarded, so this is a no-op on a PDP where they are loaded.
        HTMLUpdateUtility.setInnerHTML(this.contentEl, html);
        this.contentEl.classList.remove('hidden');
        this.hideError();
        this.hideLoading();
      } catch (error) {
        this.reportError(error);
        this.renderError();
      }
    }

    renderLoading() {
      this.contentEl?.replaceChildren();
      this.contentEl?.classList.remove('hidden');
      this.hideError();
      this.skeletonEl?.classList.remove('hidden');
    }

    hideLoading() {
      this.skeletonEl?.classList.add('hidden');
    }

    renderError() {
      this.hideLoading();
      this.contentEl?.classList.remove('hidden');
      this.errorEl?.classList.remove('hidden');
      this.errorEl?.classList.add('flex');
    }

    hideError() {
      this.errorEl?.classList.add('hidden');
      this.errorEl?.classList.remove('flex');
    }

    // Never let a fetch or parse failure take the rest of the page down with it —
    // log and fall back to the inline error state instead of throwing.
    reportError(error) {
      console.error('[quick-add-drawer]', error);
    }
  }

  customElements.define('quick-add-drawer', QuickAddDrawer);
}
